import React, { useState, useEffect, useMemo } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import SolicitudesService from "../../services/SolicitudesService";
import SucursalesService from "../../services/SucursalesService";
import ConveniosService from "../../services/ConveniosService";
import EstudiosService from "../../services/EstudiosService";
import EstudiosIntService from "../../services/EstudiosIntService";
import CajaService from "../../services/CajaService";
import TelefonosService from "../../services/TelefonosService";

import logoLaboratorio from "../../img/logo-laboratorio.jpg";
import Icon from "@mdi/react";
import { mdiFilePdfBox, mdiFileExcel } from "@mdi/js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { ejecutarConCargandoPDF } from "../../utils/pdfLoadingUtils";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  Grid,
  TextField,
  Select,
  MenuItem,
  Paper,
  Typography,
  Stack,
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const NumeroEstudios = () => {
  const getFechaHoy = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const [solicitudes, setSolicitudes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [conveniosList, setConveniosList] = useState([]);
  const [estudiosCatalogMap, setEstudiosCatalogMap] = useState({});
  const [subEstudiosMap, setSubEstudiosMap] = useState({});
  const [solicitudesEstudiosMap, setSolicitudesEstudiosMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Filtros
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState("todas");
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState("todas");
  const [mostrarNombreYSolicitud, setMostrarNombreYSolicitud] = useState("no"); // "no" | "si"
  const [buscarEstudioText, setBuscarEstudioText] = useState("");
  const [ordenarPor, setOrdenarPor] = useState("nombre"); // "nombre" | "cantidad"
  const [mostrarEstudiosPerfil, setMostrarEstudiosPerfil] = useState("no"); // "no" | "si"
  const [fechaDe, setFechaDe] = useState(getFechaHoy());
  const [fechaA, setFechaA] = useState(getFechaHoy());

  useEffect(() => {
    document.title = "Número de Estudios";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseFechaDate = (fechaRaw) => {
    if (!fechaRaw) return null;
    if (fechaRaw instanceof Date) {
      return isNaN(fechaRaw.getTime())
        ? null
        : new Date(fechaRaw.getFullYear(), fechaRaw.getMonth(), fechaRaw.getDate());
    }
    const str = String(fechaRaw).trim();
    if (!str) return null;

    const datePart = str.split(" ")[0].split("T")[0];

    if (datePart.includes("/")) {
      const parts = datePart.split("/");
      if (parts.length === 3) {
        const d = Number(parts[0]);
        const m = Number(parts[1]) - 1;
        const y = Number(parts[2]);
        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
          return new Date(y, m, d);
        }
      }
    }

    if (datePart.includes("-")) {
      const parts = datePart.split("-");
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          const y = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const d = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        } else if (parts[2].length === 4) {
          const d = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const y = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        }
      }
    }

    const dt = new Date(str);
    if (!isNaN(dt.getTime())) {
      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    }
    return null;
  };

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resSol, resSuc, resConv, resCatEst, resCaja] = await Promise.all([
        SolicitudesService.getSolicitudes().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
        ConveniosService.getConvenios().catch(() => ({ data: [] })),
        EstudiosService.getEstudios().catch(() => ({ data: [] })),
        CajaService.getCaja().catch(() => ({ data: { caja: [] } })),
      ]);

      const listaSol = Array.isArray(resSol?.data?.solicitudes)
        ? resSol.data.solicitudes
        : Array.isArray(resSol?.data)
        ? resSol.data
        : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
        ? resSuc.data
        : [];

      const listaConv = Array.isArray(resConv?.data?.convenios)
        ? resConv.data.convenios
        : Array.isArray(resConv?.data)
        ? resConv.data
        : [];

      const listaCatEst = Array.isArray(resCatEst?.data?.estudios)
        ? resCatEst.data.estudios
        : Array.isArray(resCatEst?.data)
        ? resCatEst.data
        : [];

      const listaCaja = Array.isArray(resCaja?.data?.caja)
        ? resCaja.data.caja
        : Array.isArray(resCaja?.data)
        ? resCaja.data
        : [];

      // Mapear catálogo de estudios por idEstudio
      const catMap = {};
      listaCatEst.forEach((est) => {
        if (est && est.idEstudio) {
          catMap[est.idEstudio] = est;
        }
      });
      setEstudiosCatalogMap(catMap);

      // Mapear caja por idSolicitud
      const cajaMap = {};
      listaCaja.forEach((c) => {
        if (c && c.idSolicitud) {
          cajaMap[c.idSolicitud] = c;
        }
      });

      // Unir adeudo y caja a solicitudes
      const solicitudesConCaja = listaSol.map((s) => {
        const c = cajaMap[s.idSolicitud];
        const totalSol = Number(s.totalPagar ?? s.total ?? s.monto ?? 0);
        const descSol = Number(s.descuentoTotal ?? s.descuento ?? s.descuentoMonto ?? 0);

        let adeudoVal = 0;
        if (c) {
          adeudoVal = Number(c.adeudo ?? 0);
        } else if (s.adeudo !== undefined && s.adeudo !== null) {
          adeudoVal = Number(s.adeudo);
        } else if (s.saldoPendiente !== undefined && s.saldoPendiente !== null) {
          adeudoVal = Number(s.saldoPendiente);
        } else {
          const pagadoVal = Number(s.pagado ?? 0);
          adeudoVal = Math.max(0, totalSol - descSol - pagadoVal);
        }

        return {
          ...s,
          cajaData: c,
          adeudo: adeudoVal,
          totalPagar: totalSol,
        };
      });

      setSolicitudes(solicitudesConCaja);
      setSucursales(listaSuc);
      setConveniosList(listaConv);

      // Cargar estudios para cada solicitud
      const solEstMap = {};
      const subEstMap = {};

      await Promise.all(
        solicitudesConCaja.map(async (sol) => {
          if (sol.idSolicitud) {
            try {
              const resEst = await SolicitudesService.getEstudiosPorSolicitud(sol.idSolicitud);
              const estudiosArr = resEst?.data?.estudios ?? resEst?.data?.solicitudesEstudios ?? resEst?.data ?? [];
              if (Array.isArray(estudiosArr)) {
                solEstMap[sol.idSolicitud] = estudiosArr;

                // Cargar subestudios para estudios principales
                await Promise.all(
                  estudiosArr.map(async (st) => {
                    const estId = st.idEstudio;
                    if (estId && !subEstMap[estId]) {
                      try {
                        const subRes = await EstudiosIntService.getEstudiosPorEstudio(estId);
                        if (Array.isArray(subRes) && subRes.length > 0) {
                          subEstMap[estId] = subRes;
                        }
                      } catch (e) {}
                    }
                  })
                );
              }
            } catch (e) {}
          }
        })
      );

      setSolicitudesEstudiosMap(solEstMap);
      setSubEstudiosMap(subEstMap);
    } catch (error) {
      console.error("Error cargando datos para Número de Estudios:", error);
      Swal.fire({
        title: "¡Error!",
        text: "Ocurrió un error al consultar la información del servidor.",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setLoading(false);
    }
  };

  const coincideSucursal = (sol, sucId) => {
    if (!sol || !sucId) return false;
    if (sucId === "todas") return true;

    const idSucSol = String(sol.idSucursal || sol.sucursalId || sol.sucursal || "");
    const nomSucSol = String(sol.nombreSucursal || sol.sucursal || "").trim().toLowerCase();

    const targetSuc = sucursales.find(
      (s) => String(s.idSucursal) === String(sucId) || String(s.nombre).trim().toLowerCase() === String(sucId).trim().toLowerCase()
    );

    if (targetSuc) {
      const targetNom = String(targetSuc.nombre).trim().toLowerCase();
      return idSucSol === String(targetSuc.idSucursal) || nomSucSol === targetNom;
    }
    return idSucSol === String(sucId) || nomSucSol === String(sucId).trim().toLowerCase();
  };

  const coincideEmpresa = (sol, empId) => {
    if (!sol || !empId) return false;
    if (empId === "todas") return true;

    const idConvSol = String(sol.idConvenio || sol.convenioId || "");
    const nomConvSol = String(sol.nombreConvenio || sol.convenio || "").trim().toLowerCase();

    const targetConv = conveniosList.find(
      (c) => String(c.idConvenio) === String(empId) || String(c.nombre).trim().toLowerCase() === String(empId).trim().toLowerCase()
    );

    if (targetConv) {
      const targetNom = String(targetConv.nombre).trim().toLowerCase();
      return idConvSol === String(targetConv.idConvenio) || nomConvSol === targetNom;
    }
    return idConvSol === String(empId) || nomConvSol === String(empId).trim().toLowerCase();
  };

  // Cambio dinámico en 'Mostrar el nombre y número de solicitud correspondiente al estudio'
  const handleMostrarNombreYSolicitudChange = (val) => {
    setMostrarNombreYSolicitud(val);
    if (val === "si") {
      setOrdenarPor("nombre");
      setMostrarEstudiosPerfil("no");
    }
  };

  // Filtrado de solicitudes válidas por rango de fechas, sucursal y empresa
  const solicitudesFiltradas = useMemo(() => {
    const start = parseFechaDate(fechaDe <= fechaA ? fechaDe : fechaA);
    const end = parseFechaDate(fechaDe <= fechaA ? fechaA : fechaDe);
    if (!start || !end) return [];

    return solicitudes.filter((sol) => {
      // Excluir canceladas
      const estatus = `${sol.estatus ?? ""}`.trim().toUpperCase();
      if (estatus === "C" || (sol.motivoCancelacion && sol.motivoCancelacion.trim() !== "")) {
        return false;
      }

      // Validar sucursal
      if (!coincideSucursal(sol, sucursalSeleccionada)) return false;

      // Validar empresa / convenio
      if (!coincideEmpresa(sol, empresaSeleccionada)) return false;

      // Validar rango de fechas
      const dt = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.created_at || sol.createdAt);
      if (!dt) return false;
      if (dt.getTime() < start.getTime() || dt.getTime() > end.getTime()) return false;

      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudes, sucursalSeleccionada, empresaSeleccionada, fechaDe, fechaA, sucursales, conveniosList]);

  // MODO A: Agrupado por estudio (cuando mostrarNombreYSolicitud === "no")
  const estudiosAgrupados = useMemo(() => {
    if (mostrarNombreYSolicitud === "si") return [];

    const groupMap = new Map();

    solicitudesFiltradas.forEach((sol) => {
      const estArr = solicitudesEstudiosMap[sol.idSolicitud] || [];
      const padres = estArr.filter((e) => !e.idEstudioPadre || Number(e.idEstudioPadre) === 0);

      padres.forEach((st) => {
        const idEst = st.idEstudio;
        const catInfo = estudiosCatalogMap[idEst] || {};
        const codigo = st.codigo || st.codigoEstudio || catInfo.codigo || `EST-${idEst}`;
        const nombre = st.nombreEstudio || st.nombre || catInfo.nombre || `Estudio #${idEst}`;

        if (!groupMap.has(idEst)) {
          groupMap.set(idEst, {
            idEstudio: idEst,
            codigo: codigo,
            nombre: nombre,
            cantidad: 0,
            subEstudios: subEstudiosMap[idEst] || [],
          });
        }
        const currentGroup = groupMap.get(idEst);
        currentGroup.cantidad += 1;
      });
    });

    let list = Array.from(groupMap.values());

    // Filtrar por texto de búsqueda
    if (buscarEstudioText.trim() !== "") {
      const busq = buscarEstudioText.trim().toLowerCase();
      list = list.filter(
        (e) => e.codigo.toLowerCase().includes(busq) || e.nombre.toLowerCase().includes(busq)
      );
    }

    // Ordenar por nombre o por cantidad
    if (ordenarPor === "nombre") {
      list.sort((a, b) => a.nombre.localeCompare(b.nombre));
    } else if (ordenarPor === "cantidad") {
      list.sort((a, b) => b.cantidad - a.cantidad);
    }

    return list;
  }, [solicitudesFiltradas, solicitudesEstudiosMap, estudiosCatalogMap, subEstudiosMap, buscarEstudioText, ordenarPor, mostrarNombreYSolicitud]);

  // MODO B: Desglosado por estudio individual (cuando mostrarNombreYSolicitud === "si")
  const estudiosDesglosadosDetalle = useMemo(() => {
    if (mostrarNombreYSolicitud === "no") return [];

    const list = [];

    solicitudesFiltradas.forEach((sol) => {
      const estArr = solicitudesEstudiosMap[sol.idSolicitud] || [];
      const padres = estArr.filter((e) => !e.idEstudioPadre || Number(e.idEstudioPadre) === 0);

      const pacienteNom = `${sol.nombrePaciente || ""} ${sol.apPaternoPaciente || ""} ${sol.apMaternoPaciente || ""}`.trim();
      const tieneEmpresa = sol.nombreConvenio && sol.nombreConvenio.trim() !== "";
      const pacienteText = tieneEmpresa ? `${pacienteNom} (${sol.nombreConvenio.trim()})` : pacienteNom;
      const estadoPago = Number(sol.adeudo ?? 0) <= 0.001 ? "PAGADO" : "PAGO PENDIENTE";

      padres.forEach((st) => {
        const idEst = st.idEstudio;
        const catInfo = estudiosCatalogMap[idEst] || {};
        const codigo = st.codigo || st.codigoEstudio || catInfo.codigo || `EST-${idEst}`;
        const nombre = st.nombreEstudio || st.nombre || catInfo.nombre || `Estudio #${idEst}`;

        const costoUnit = Number(st.costo || st.precio || catInfo.precio || catInfo.costo || 0);
        const descUnit = Number(st.descuento || 0);
        const importe = Math.max(0, costoUnit - descUnit);

        list.push({
          idSolicitud: sol.idSolicitud,
          codigoSolicitud: sol.codigo || sol.idSolicitud,
          paciente: pacienteText,
          estadoPago: estadoPago,
          idEstudio: idEst,
          codigoEstudio: codigo,
          nombreEstudio: nombre,
          costoUnitario: costoUnit,
          descuentoUnitario: descUnit,
          importe: importe,
        });
      });
    });

    // Filtrar por texto de búsqueda
    let filteredList = list;
    if (buscarEstudioText.trim() !== "") {
      const busq = buscarEstudioText.trim().toLowerCase();
      filteredList = list.filter(
        (item) =>
          item.codigoEstudio.toLowerCase().includes(busq) ||
          item.nombreEstudio.toLowerCase().includes(busq) ||
          String(item.codigoSolicitud).toLowerCase().includes(busq) ||
          item.paciente.toLowerCase().includes(busq)
      );
    }

    // Ordenar alfabéticamente por nombre del estudio
    filteredList.sort((a, b) => a.nombreEstudio.localeCompare(b.nombreEstudio));

    return filteredList;
  }, [solicitudesFiltradas, solicitudesEstudiosMap, estudiosCatalogMap, buscarEstudioText, mostrarNombreYSolicitud]);

  // Texto del encabezado principal de la tabla
  const tituloHeaderTabla = useMemo(() => {
    let sucNom = "Todas las sucursales";
    if (sucursalSeleccionada !== "todas") {
      const found = sucursales.find(
        (s) =>
          String(s.idSucursal) === String(sucursalSeleccionada) ||
          String(s.nombre).trim().toLowerCase() === String(sucursalSeleccionada).trim().toLowerCase()
      );
      if (found) sucNom = (found.nombre || "").toUpperCase();
    }
    return `${sucNom} Del (${fechaDe}) al (${fechaA})`;
  }, [sucursalSeleccionada, sucursales, fechaDe, fechaA]);

  // Restablecer filtros
  const handleRestablecer = () => {
    setSucursalSeleccionada("todas");
    setEmpresaSeleccionada("todas");
    setMostrarNombreYSolicitud("no");
    setBuscarEstudioText("");
    setOrdenarPor("nombre");
    setMostrarEstudiosPerfil("no");
    setFechaDe(getFechaHoy());
    setFechaA(getFechaHoy());
  };

  // Exportación a PDF Institucional
  const handleExportarPDF = async () => {
    const hayDatos = mostrarNombreYSolicitud === "no" ? estudiosAgrupados.length > 0 : estudiosDesglosadosDetalle.length > 0;
    if (!hayDatos) {
      Swal.fire({
        title: "Sin datos",
        text: "No existen registros para los filtros seleccionados para exportar a PDF.",
        icon: "info",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    await ejecutarConCargandoPDF(
      async () => {
        const doc = new jsPDF();
        const startY = 12;

    let sucursalActualObj = sucursales.find(
      (s) =>
        String(s.idSucursal) === String(sucursalSeleccionada) ||
        String(s.nombre).trim().toLowerCase() === String(sucursalSeleccionada).trim().toLowerCase()
    ) || sucursales[0] || {};

    let sucursalTelefonosStr = "";
    if (sucursalActualObj.idSucursal) {
      try {
        const resTels = await TelefonosService.getTelefonosPorSucursal(sucursalActualObj.idSucursal);
        const listaTels = Array.isArray(resTels) ? resTels : resTels?.telefonos || [];
        sucursalTelefonosStr = listaTels.map((t) => t.telefono || t).join(", ");
      } catch (e) {}
    }

    const sucursalNomHeader = sucursalSeleccionada === "todas" ? "TODAS LAS SUCURSALES" : (sucursalActualObj.nombre || "SUCURSAL").toUpperCase();
    const calleText = [sucursalActualObj.calle, sucursalActualObj.numExterior, sucursalActualObj.numInterior].filter(Boolean).join(" ");
    const colCpText = `Col. ${sucursalActualObj.colonia || ""} C.P. ${sucursalActualObj.cPostal || ""} ${sucursalActualObj.ciudad || ""}, ${sucursalActualObj.estado || ""}`;
    const telText = `Tel. ${sucursalTelefonosStr || sucursalActualObj.telefono || ""}`;

    // Dibujar Logo
    try {
      doc.addImage(logoLaboratorio, "JPEG", 14, startY, 45, 28);
    } catch (e) {}

    // Rectángulo institucional "INFORMES"
    doc.setFillColor(24, 35, 79);
    doc.rect(115, startY, 81, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("INFORMES", 155.5, startY + 5, { align: "center" });

    // Datos sucursal a la derecha
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(sucursalNomHeader, 196, startY + 12, { align: "right" });

    let currY = startY + 16;
    if (calleText.trim()) {
      doc.setFont("helvetica", "normal");
      doc.text(`Dirección: ${calleText}`, 196, currY, { align: "right" });
      currY += 4;
    }
    doc.setFont("helvetica", "normal");
    doc.text(colCpText, 196, currY, { align: "right" });
    currY += 4;
    doc.text(telText, 196, currY, { align: "right" });

    // Título
    currY += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(24, 35, 79);
    doc.text("NÚMERO DE ESTUDIOS POR SUCURSAL", 14, currY);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Período del ${fechaDe} al ${fechaA}`, 14, currY + 4);

    let nextY = currY + 10;
    const filasPdf = [];

    if (mostrarNombreYSolicitud === "no") {
      estudiosAgrupados.forEach((item) => {
        filasPdf.push([item.codigo, item.nombre, item.cantidad]);

        if (mostrarEstudiosPerfil === "si" && item.subEstudios && item.subEstudios.length > 0) {
          item.subEstudios.forEach((sub) => {
            const nomSub = sub.nombreEstudioSec || sub.nombre || sub.nombreEstudio || "Subestudio";
            filasPdf.push([
              "",
              `** ${nomSub}`,
              "",
            ]);
          });
        }
      });

      autoTable(doc, {
        startY: nextY,
        head: [
          [
            { content: tituloHeaderTabla, colSpan: 3, styles: { halign: "center", fillColor: [24, 35, 79], textColor: [255, 255, 255], fontStyle: "bold" } },
          ],
          ["Código", "Estudio", "# Estudios realizados"],
        ],
        body: filasPdf,
        headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
        styles: { fontSize: 7, cellPadding: 2, halign: "left" },
        columnStyles: { 0: { fontStyle: "bold", width: 35 }, 2: { halign: "center" } },
      });
    } else {
      let totalCosto = 0;
      let totalDesc = 0;
      let totalImporte = 0;

      estudiosDesglosadosDetalle.forEach((item) => {
        totalCosto += item.costoUnitario;
        totalDesc += item.descuentoUnitario;
        totalImporte += item.importe;

        filasPdf.push([
          item.codigoEstudio,
          item.nombreEstudio,
          item.codigoSolicitud,
          item.paciente,
          item.estadoPago,
          `$${item.costoUnitario.toFixed(2)}`,
          `$${item.descuentoUnitario.toFixed(2)}`,
          `$${item.importe.toFixed(2)}`,
        ]);
      });

      filasPdf.push([
        { content: "Totales:", colSpan: 5, styles: { fontStyle: "bold", halign: "right", fillColor: [238, 242, 246] } },
        `$${totalCosto.toFixed(2)}`,
        `$${totalDesc.toFixed(2)}`,
        `$${totalImporte.toFixed(2)}`,
      ]);

      autoTable(doc, {
        startY: nextY,
        head: [
          [
            { content: tituloHeaderTabla, colSpan: 8, styles: { halign: "center", fillColor: [24, 35, 79], textColor: [255, 255, 255], fontStyle: "bold" } },
          ],
          [
            "Código",
            "Estudio",
            "Solicitud",
            "Paciente",
            "Estado",
            "Costo Unitario",
            "Descuento Unitario",
            "Importe",
          ],
        ],
        body: filasPdf,
        headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.5 },
        styles: { fontSize: 6.5, cellPadding: 1.5, halign: "center" },
        columnStyles: { 0: { halign: "left", fontStyle: "bold" }, 1: { halign: "left" }, 3: { halign: "left" } },
      });
    }

    // Pie de Página
    const pageCount = doc.internal.getNumberOfPages();
    const hoyImpStr = new Date().toLocaleString();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Impreso: ${hoyImpStr}`, 14, 287);
      doc.text(`Página ${i} de ${pageCount}`, 196, 287, { align: "right" });
    }

        doc.save(`Numero_Estudios_${fechaDe}_al_${fechaA}.pdf`);
      },
      "Generando reporte de Número de Estudios en PDF...",
      "Procesando documento"
    );
  };

  // Exportación a Excel (.xlsx)
  const handleExportarExcel = () => {
    const hayDatos = mostrarNombreYSolicitud === "no" ? estudiosAgrupados.length > 0 : estudiosDesglosadosDetalle.length > 0;
    if (!hayDatos) {
      Swal.fire({
        title: "Sin datos",
        text: "No existen registros para los filtros seleccionados para exportar a Excel.",
        icon: "info",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    const rowsExcel = [];

    if (mostrarNombreYSolicitud === "no") {
      rowsExcel.push(["Código", "Estudio", "# Estudios realizados"]);

      estudiosAgrupados.forEach((item) => {
        rowsExcel.push([item.codigo, item.nombre, item.cantidad]);

        if (mostrarEstudiosPerfil === "si" && item.subEstudios && item.subEstudios.length > 0) {
          item.subEstudios.forEach((sub) => {
            const nomSub = sub.nombreEstudioSec || sub.nombre || sub.nombreEstudio || "Subestudio";
            rowsExcel.push(["", `** ${nomSub}`, ""]);
          });
        }
      });
    } else {
      rowsExcel.push([
        "Código",
        "Estudio",
        "Solicitud",
        "Paciente",
        "Estado",
        "Costo Unitario",
        "Descuento Unitario",
        "Importe",
      ]);

      let totalCosto = 0;
      let totalDesc = 0;
      let totalImporte = 0;

      estudiosDesglosadosDetalle.forEach((item) => {
        totalCosto += item.costoUnitario;
        totalDesc += item.descuentoUnitario;
        totalImporte += item.importe;

        rowsExcel.push([
          item.codigoEstudio,
          item.nombreEstudio,
          item.codigoSolicitud,
          item.paciente,
          item.estadoPago,
          item.costoUnitario,
          item.descuentoUnitario,
          item.importe,
        ]);
      });

      rowsExcel.push([
        "TOTALES",
        "",
        "",
        "",
        "",
        totalCosto,
        totalDesc,
        totalImporte,
      ]);
    }

    const worksheet = XLSX.utils.aoa_to_sheet(rowsExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "NumeroEstudios");

    XLSX.writeFile(workbook, `Numero_Estudios_${fechaDe}_al_${fechaA}.xlsx`);
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "80vh",
            py: 3,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  NÚMERO DE ESTUDIOS POR SUCURSAL
                </Typography>
              </Box>

              {/* Filtros de Búsqueda */}
              <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                <Table
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": { padding: "10px 14px" },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}>
                        Filtros del Informe
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Fila Sucursal */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "25%", backgroundColor: "#f8f9fa" }}>
                        Sucursal:
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={sucursalSeleccionada}
                          onChange={(e) => setSucursalSeleccionada(e.target.value)}
                          sx={{ width: "320px" }}
                        >
                          <MenuItem value="todas">Todas las sucursales</MenuItem>
                          {sucursales.map((suc) => (
                            <MenuItem key={suc.idSucursal} value={String(suc.idSucursal)}>
                              {suc.nombre}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Empresa */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Empresa:
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={empresaSeleccionada}
                          onChange={(e) => setEmpresaSeleccionada(e.target.value)}
                          sx={{ width: "320px" }}
                        >
                          <MenuItem value="todas">Todas</MenuItem>
                          {conveniosList.map((conv) => (
                            <MenuItem key={conv.idConvenio} value={String(conv.idConvenio)}>
                              {conv.nombre}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Mostrar el nombre y número de solicitud correspondiente al estudio */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Mostrar el nombre y número de solicitud correspondiente al estudio:
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={mostrarNombreYSolicitud}
                          onChange={(e) => handleMostrarNombreYSolicitudChange(e.target.value)}
                          sx={{ width: "160px" }}
                        >
                          <MenuItem value="no">No</MenuItem>
                          <MenuItem value="si">Si</MenuItem>
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Buscar estudio */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Buscar estudio:
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Buscar por código o nombre de estudio..."
                          value={buscarEstudioText}
                          onChange={(e) => setBuscarEstudioText(e.target.value)}
                          sx={{ width: "380px" }}
                        />
                      </TableCell>
                    </TableRow>

                    {/* Fila Ordenar Por */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Ordenar Por:
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={ordenarPor}
                          onChange={(e) => setOrdenarPor(e.target.value)}
                          disabled={mostrarNombreYSolicitud === "si"}
                          sx={{ width: "260px" }}
                        >
                          <MenuItem value="nombre">Por nombre del estudio</MenuItem>
                          <MenuItem value="cantidad">Por cantidad de estudios</MenuItem>
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Mostrar estudios que conforman los perfiles */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Mostrar estudios que conforman los perfiles:
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={mostrarEstudiosPerfil}
                          onChange={(e) => setMostrarEstudiosPerfil(e.target.value)}
                          disabled={mostrarNombreYSolicitud === "si"}
                          sx={{ width: "160px" }}
                        >
                          <MenuItem value="no">No</MenuItem>
                          <MenuItem value="si">Si</MenuItem>
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Rango de Fechas */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Seleccione un rango de fechas:
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={3} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            De:
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            value={fechaDe}
                            onChange={(e) => setFechaDe(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: "180px" }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            A:
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            value={fechaA}
                            onChange={(e) => setFechaA(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: "180px" }}
                          />

                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleRestablecer}
                            startIcon={<RefreshIcon />}
                            sx={{ backgroundColor: "#18234f", color: "white", ml: 2 }}
                          >
                            Restablecer
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Tabla de Resultados */}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress sx={{ color: "#006787" }} />
                </Box>
              ) : (
                <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                  <Table
                    sx={{
                      width: "100%",
                      backgroundColor: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      overflow: "hidden",
                      "& .MuiTableCell-root": { padding: "8px 10px", fontSize: "0.82rem" },
                    }}
                  >
                    <TableHead>
                      {/* Fila 1: Título con Sucursal y Rango de Fechas */}
                      <TableRow sx={{ backgroundColor: "#18234f" }}>
                        <TableCell
                          colSpan={mostrarNombreYSolicitud === "no" ? 3 : 8}
                          align="center"
                          sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}
                        >
                          {tituloHeaderTabla}
                        </TableCell>
                      </TableRow>

                      {/* Fila 2: Cabeceras de columnas */}
                      {mostrarNombreYSolicitud === "no" ? (
                        <TableRow sx={{ backgroundColor: "#006787" }}>
                          <TableCell sx={{ color: "white", fontWeight: "bold", width: "20%" }}>Código</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Estudio</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold", width: "25%" }}>
                            # Estudios realizados
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow sx={{ backgroundColor: "#006787" }}>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Código</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Estudio</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Solicitud</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Paciente</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Estado</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Costo unitario</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Descuento unitario</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Importe</TableCell>
                        </TableRow>
                      )}
                    </TableHead>

                    <TableBody>
                      {mostrarNombreYSolicitud === "no" ? (
                        // MODO A: Agrupado por Estudio
                        estudiosAgrupados.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ color: "#888", py: 4 }}>
                              No hay estudios registrados para los filtros seleccionados.
                            </TableCell>
                          </TableRow>
                        ) : (
                          estudiosAgrupados.map((item) => (
                            <React.Fragment key={item.idEstudio}>
                              <TableRow sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}>
                                <TableCell sx={{ fontWeight: "bold" }}>{item.codigo}</TableCell>
                                <TableCell>{item.nombre}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", color: "#006787" }}>
                                  {item.cantidad}
                                </TableCell>
                              </TableRow>

                              {/* Subestudios si está activo Mostrar estudios que conforman los perfiles */}
                              {mostrarEstudiosPerfil === "si" && item.subEstudios && item.subEstudios.length > 0 && (
                                item.subEstudios.map((sub, idx) => {
                                  const nomSub = sub.nombreEstudioSec || sub.nombre || sub.nombreEstudio || "Subestudio";
                                  return (
                                    <TableRow key={`${item.idEstudio}-sub-${idx}`} sx={{ backgroundColor: "#f9fafb" }}>
                                      <TableCell />
                                      <TableCell colSpan={2} sx={{ pl: 4, fontStyle: "italic", color: "#555" }}>
                                        ** {nomSub}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                              )}
                            </React.Fragment>
                          ))
                        )
                      ) : (
                        // MODO B: Desglosado por Solicitud
                        estudiosDesglosadosDetalle.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ color: "#888", py: 4 }}>
                              No hay estudios registrados para los filtros seleccionados.
                            </TableCell>
                          </TableRow>
                        ) : (
                          <>
                            {estudiosDesglosadosDetalle.map((item, idx) => (
                              <TableRow key={idx} sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}>
                                <TableCell sx={{ fontWeight: "bold" }}>{item.codigoEstudio}</TableCell>
                                <TableCell>{item.nombreEstudio}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{item.codigoSolicitud}</TableCell>
                                <TableCell>{item.paciente}</TableCell>
                                <TableCell align="center">
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      px: 1,
                                      py: 0.3,
                                      borderRadius: "4px",
                                      fontWeight: "bold",
                                      backgroundColor: item.estadoPago === "PAGADO" ? "#e8f5e9" : "#ffebee",
                                      color: item.estadoPago === "PAGADO" ? "#2e7d32" : "#c62828",
                                    }}
                                  >
                                    {item.estadoPago}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">${item.costoUnitario.toFixed(2)}</TableCell>
                                <TableCell align="center" sx={{ color: "#d32f2f" }}>${item.descuentoUnitario.toFixed(2)}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", color: "#18234f" }}>${item.importe.toFixed(2)}</TableCell>
                              </TableRow>
                            ))}

                            {/* Fila Totales */}
                            <TableRow sx={{ backgroundColor: "#eef2f6", borderTop: "2px solid #18234f" }}>
                              <TableCell colSpan={5} sx={{ fontWeight: "bold", textAlign: "right" }}>Totales:</TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                ${estudiosDesglosadosDetalle.reduce((acc, i) => acc + i.costoUnitario, 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                                ${estudiosDesglosadosDetalle.reduce((acc, i) => acc + i.descuentoUnitario, 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#18234f" }}>
                                ${estudiosDesglosadosDetalle.reduce((acc, i) => acc + i.importe, 0).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          </>
                        )
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Dos Botones Flotantes Laterales Derechos: PDF y Excel */}
        <Box
          sx={{
            position: "fixed",
            right: "3vw",
            top: "35vh",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          {/* Botón PDF */}
          <Tooltip
            title="Exportar Reporte a PDF"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarPDF}
              sx={{
                backgroundColor: "#ffffff",
                color: "#d32f2f",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFilePdfBox} size={1.5} color="#d32f2f" />
            </IconButton>
          </Tooltip>

          {/* Botón Excel */}
          <Tooltip
            title="Exportar Reporte a Excel"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarExcel}
              sx={{
                backgroundColor: "#ffffff",
                color: "#2e7d32",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFileExcel} size={1.5} color="#2e7d32" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default NumeroEstudios;
